import { CalendarDaysIcon, ListBulletIcon } from "@heroicons/react/20/solid";
import moment from "moment";
import React from "react";
import RP_Avatar from "../RP_Avatar";

const StatusPill = ({ sent }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
      sent
        ? "bg-wa-accent/15 text-wa-accent"
        : "bg-red-500/15 text-red-400"
    }`}
  >
    {sent ? "Verstuurd" : "Niet verstuurd"}
  </span>
);

const DashboardlistPatient = ({ patienten, goToDetails }) => {
  if (!patienten?.length) {
    return (
      <p className="rounded-xl border border-dashed border-wa-border bg-wa-panel/50 py-12 text-center text-sm text-wa-muted">
        Geen patiënten gevonden.
      </p>
    );
  }

  return (
    <>
      <ul className="space-y-3 lg:hidden">
        {patienten.map((person, index) => (
          <li
            key={person?.userdetails_user_id}
            onClick={() => goToDetails(person)}
            style={{ animationDelay: `${Math.min(index * 60, 360)}ms` }}
            className="rounded-xl border border-wa-border bg-wa-panel2 p-4 opacity-0 shadow-sm shadow-black/5 animate-patient-card-in motion-reduce:animate-none motion-reduce:opacity-100"
          >
            <div className="flex items-center gap-3">
              <RP_Avatar
                avatar={person?.userdetails_avatar}
                size={12}
                alt={person?.userdetails_full_name}
              />
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-wa-text">
                  {person?.userdetails_full_name}
                </p>
                <p className="truncate text-sm text-wa-text/70">
                  {person?.userdetails_email}
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
              <div className="rounded-lg border border-wa-border bg-wa-panel p-2.5">
                <p className="mb-2 flex items-center gap-1 font-semibold uppercase tracking-wide text-wa-muted">
                  <ListBulletIcon className="h-4 w-4" />
                  Vragenlijsten
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-wa-text/80">Preintake</span>
                    <StatusPill sent={person?.vragenlijsten_preintake_verstuur == 1} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-wa-text/80">Klacht</span>
                    <StatusPill sent={person?.vragenlijsten_klacht_verstuur == 1} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-wa-text/80">Voeding</span>
                    <StatusPill sent={person?.vragenlijsten_voeding_verstuur == 1} />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-wa-border bg-wa-panel p-2.5">
                <p className="mb-2 font-semibold uppercase tracking-wide text-wa-muted">
                  Eetdagboek
                </p>
                {person?.eetdagboek_title ? (
                  <span className="inline-flex max-w-full items-center truncate rounded-md border border-wa-accent/40 bg-wa-accentdeep/30 px-2 py-1 text-xs font-medium text-wa-text">
                    {person?.eetdagboek_title}
                  </span>
                ) : (
                  <span className="text-wa-text/70">Geen titel</span>
                )}

                <p className="mb-2 mt-3 flex items-center gap-1 font-semibold uppercase tracking-wide text-wa-muted">
                  <CalendarDaysIcon className="h-4 w-4" />
                  Afspraak
                </p>
                <div className="text-wa-text/80">
                  {person?.appointment_date ? (
                    <>
                      <p className="text-wa-text">
                        {moment(person?.appointment_date).format("DD/MM/YYYY HH:mm")}
                      </p>
                      <p className="mt-0.5 text-xs">{person?.appointment_type}</p>
                    </>
                  ) : (
                    <p>Geen afspraak gepland</p>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden overflow-x-auto rounded-xl border border-wa-border bg-wa-panel2 shadow-lg shadow-black/10 lg:block">
        <table className="w-full min-w-[860px] divide-y divide-wa-border">
          <thead>
            <tr className="bg-wa-panel">
              <th
                scope="col"
                className="py-3.5 pl-6 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-wa-muted"
              >
                Naam
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-wa-muted"
              >
                <div className="flex items-center gap-2">
                  Vragenlijsten
                  <ListBulletIcon className="h-4 w-4 text-wa-muted" />
                </div>
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-wa-muted"
              >
                Eetdagboek
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-wa-muted"
              >
                <div className="flex items-center gap-2">
                  Afspraak
                  <CalendarDaysIcon className="h-4 w-4 text-wa-muted" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-wa-border bg-wa-panel2">
            {patienten.map((person, index) => (
              <tr
                className="cursor-pointer opacity-0 animate-patient-card-in motion-reduce:animate-none motion-reduce:opacity-100 transition-colors hover:bg-wa-rowhover"
                style={{ animationDelay: `${Math.min(index * 50, 400)}ms` }}
                key={person?.userdetails_user_id}
                onClick={() => goToDetails(person)}
              >
                <td className="whitespace-nowrap py-5 pl-6 pr-3 text-xs">
                  <div className="flex items-center">
                    <RP_Avatar
                      avatar={person?.userdetails_avatar}
                      size={11}
                      alt={person?.userdetails_full_name}
                    />
                    <div className="ml-3 min-w-0">
                      <div className="truncate text-sm font-semibold text-wa-text">
                        {person?.userdetails_full_name}
                      </div>
                      <div className="mt-0.5 truncate text-sm text-wa-text/70">
                        {person?.userdetails_email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="max-w-xs whitespace-normal px-3 py-5 text-xs text-wa-muted">
                  <ul className="space-y-2">
                    <li className="flex items-center justify-between gap-3">
                      <span className="text-wa-text/80">Preintake</span>
                      <StatusPill sent={person?.vragenlijsten_preintake_verstuur == 1} />
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="text-wa-text/80">Klacht</span>
                      <StatusPill sent={person?.vragenlijsten_klacht_verstuur == 1} />
                    </li>
                    <li className="flex items-center justify-between gap-3">
                      <span className="text-wa-text/80">Voeding</span>
                      <StatusPill sent={person?.vragenlijsten_voeding_verstuur == 1} />
                    </li>
                  </ul>
                </td>
                <td className="whitespace-nowrap px-3 py-5 text-xs">
                  {person?.eetdagboek_title ? (
                    <span className="inline-flex max-w-[12rem] items-center truncate rounded-md border border-wa-accent/40 bg-wa-accentdeep/30 px-2 py-1 text-xs font-medium text-wa-text">
                      {person?.eetdagboek_title}
                    </span>
                  ) : (
                    <span className="text-wa-text/70">Geen titel</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-5 text-xs text-wa-text/70">
                  {person?.appointment_date ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-wa-text">
                        {moment(person?.appointment_date).format("DD/MM/YYYY HH:mm")}
                      </span>
                      <span>{person?.appointment_type}</span>
                    </div>
                  ) : (
                    <span>Geen afspraak gepland</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default DashboardlistPatient;
